(() => {
  if (window.rzCustomerStoriesLoaded) return;
  window.rzCustomerStoriesLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initialize = (root) => {
    if (!root || root.dataset.rzStoriesReady === 'true') return;

    const track = root.querySelector('[data-rz-stories-track]');
    const slides = Array.from(root.querySelectorAll('[data-rz-stories-slide]'));
    const previous = root.querySelector('[data-rz-stories-previous]');
    const next = root.querySelector('[data-rz-stories-next]');
    const dots = Array.from(root.querySelectorAll('[data-rz-stories-dot]'));

    if (!track || slides.length === 0) return;
    root.dataset.rzStoriesReady = 'true';

    const autoplayEnabled = root.dataset.autoplay === 'true' && !reducedMotion.matches;
    const autoplaySeconds = Math.max(4, Number(root.dataset.autoplaySeconds) || 6);
    let currentIndex = 0;
    let frame;
    let timer;

    const slidePosition = (slide) => slide.offsetLeft - track.offsetLeft;

    const nearestIndex = () => {
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      slides.forEach((slide, index) => {
        const distance = Math.abs(slidePosition(slide) - track.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const update = (index = nearestIndex()) => {
      currentIndex = index;
      root.classList.toggle('is-static', slides.length < 2);

      slides.forEach((slide, slideIndex) => {
        if (slideIndex === currentIndex) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });

      dots.forEach((dot, dotIndex) => {
        if (dotIndex === currentIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => update());
    };

    const scrollToIndex = (index, behavior = 'smooth') => {
      const wrappedIndex = (index + slides.length) % slides.length;
      currentIndex = wrappedIndex;
      update(wrappedIndex);
      track.scrollTo({
        left: slidePosition(slides[wrappedIndex]),
        behavior: reducedMotion.matches ? 'auto' : behavior
      });
    };

    const stopAutoplay = () => {
      window.clearInterval(timer);
      timer = undefined;
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!autoplayEnabled || slides.length < 2 || document.hidden) return;
      timer = window.setInterval(() => scrollToIndex(currentIndex + 1), autoplaySeconds * 1000);
    };

    previous?.addEventListener('click', () => {
      scrollToIndex(currentIndex - 1);
      startAutoplay();
    });

    next?.addEventListener('click', () => {
      scrollToIndex(currentIndex + 1);
      startAutoplay();
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        scrollToIndex(Number(dot.dataset.index));
        startAutoplay();
      });
    });

    track.addEventListener('scroll', scheduleUpdate, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      scrollToIndex(currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
      startAutoplay();
    });

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', (event) => {
      if (!root.contains(event.relatedTarget)) startAutoplay();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => scrollToIndex(currentIndex, 'auto'));
      observer.observe(track);
    } else {
      window.addEventListener('resize', () => scrollToIndex(currentIndex, 'auto'));
    }

    root.rzStoriesScrollToBlock = (blockId) => {
      const slide = slides.find((item) => item.dataset.blockId === blockId);
      if (slide) scrollToIndex(slides.indexOf(slide));
    };

    update(0);
    startAutoplay();
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-rz-stories]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-rz-stories]');
    root?.rzStoriesScrollToBlock?.(event.detail.blockId);
  });
})();
